import { useParams, Link } from "react-router-dom";
import { mockProjects } from "../../../mocks/projects";
import Header from "../../../components/feature/Header";
import Footer from "../../../components/feature/Footer";

export default function ProjectDetail() {
  const { id } = useParams<{ id: string }>();
  const project = mockProjects.find((p) => p.id === id);
  const related = mockProjects.filter((p) => p.id !== id).slice(0, 3);

  if (!project) {
    return (
      <div className="bg-arch-cream font-sans min-h-screen">
        <Header />
        <div className="flex items-center justify-center min-h-screen flex-col gap-6">
          <p className="font-serif text-4xl text-arch-charcoal font-light">Project not found</p>
          <Link to="/projects" className="text-arch-gray text-sm underline cursor-pointer">Back to Projects</Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bg-arch-cream font-sans">
      <Header />
      <main>
        {/* Hero image */}
        <section className="relative h-[70vh] min-h-[520px] overflow-hidden">
          <img
            src={project.image}
            alt={project.title + " — Studio 1323"}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-black/20" />
          <div className="absolute bottom-10 left-8 lg:left-14 right-8 lg:right-14">
            <p className="text-white/60 text-xs tracking-[0.2em] uppercase mb-2">
              {project.category} &nbsp;·&nbsp; {project.location} &nbsp;·&nbsp; {project.year}
            </p>
            <h1 className="font-serif text-4xl lg:text-6xl text-white font-light leading-tight max-w-2xl">
              {project.title}
            </h1>
          </div>
        </section>

        {/* Project info */}
        <section className="py-16 lg:py-20 px-8 lg:px-14 bg-white">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Description */}
            <div className="lg:col-span-2">
              <h2 className="font-serif text-2xl text-arch-charcoal font-light mb-4">About the Project</h2>
              <p className="text-arch-gray text-sm font-light leading-relaxed mb-4">{project.description}</p>
              <p className="text-arch-gray text-sm font-light leading-relaxed">{project.fullDescription}</p>
            </div>
            {/* Specs */}
            <div className="border-l border-arch-border pl-8 space-y-6">
              <div>
                <p className="text-arch-light text-xs tracking-wider uppercase mb-1">Project Type</p>
                <p className="text-arch-charcoal text-sm font-medium">{project.category}</p>
              </div>
              <div>
                <p className="text-arch-light text-xs tracking-wider uppercase mb-1">Location</p>
                <p className="text-arch-charcoal text-sm font-medium">{project.location}</p>
              </div>
              <div>
                <p className="text-arch-light text-xs tracking-wider uppercase mb-1">Year</p>
                <p className="text-arch-charcoal text-sm font-medium">{project.year}</p>
              </div>
              <div>
                <p className="text-arch-light text-xs tracking-wider uppercase mb-1">Square Footage</p>
                <p className="text-arch-charcoal text-sm font-medium">{project.sqft} sq ft</p>
              </div>
              <div>
                <p className="text-arch-light text-xs tracking-wider uppercase mb-2">Services Provided</p>
                <ul className="space-y-1.5">
                  {project.services.map((s) => (
                    <li key={s} className="flex items-center gap-2 text-sm text-arch-gray font-light">
                      <i className="ri-circle-fill text-arch-light text-[6px]" />
                      {s}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="pt-4">
                <Link
                  to="/contact"
                  className="w-full flex items-center justify-center gap-2 bg-arch-charcoal text-white py-3.5 text-xs tracking-[0.15em] uppercase font-medium hover:bg-arch-black transition-all rounded-md cursor-pointer whitespace-nowrap"
                >
                  Start a Similar Project
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Tags */}
        <section className="px-8 lg:px-14 pb-10 bg-white">
          <div className="flex items-center gap-2 flex-wrap">
            {project.tags.map((tag) => (
              <span key={tag} className="border border-arch-border text-arch-gray text-xs px-3 py-1.5 rounded-full font-light tracking-wide">
                {tag}
              </span>
            ))}
          </div>
        </section>

        {/* Related projects */}
        <section className="py-16 lg:py-20 px-8 lg:px-14 bg-arch-cream border-t border-arch-border">
          <div className="flex items-end justify-between mb-10">
            <h2 className="font-serif text-2xl text-arch-charcoal font-light">More Projects</h2>
            <Link to="/projects" className="text-arch-gray text-xs uppercase tracking-wider hover:text-arch-charcoal transition-colors cursor-pointer">
              View All
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {related.map((p) => (
              <Link key={p.id} to={`/projects/${p.id}`} className="group block cursor-pointer">
                <div className="w-full h-52 overflow-hidden rounded-lg mb-3">
                  <img src={p.thumb} alt={p.title + " Studio 1323"} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-600" />
                </div>
                <p className="text-arch-light text-xs uppercase tracking-wider mb-1">{p.category}</p>
                <h3 className="font-serif text-lg text-arch-charcoal font-light">{p.title}</h3>
              </Link>
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
